import React, { useEffect, useState } from "react";
import api from "../utils/api";
import { API_ENDPOINTS } from "../utils/urls";
import { toast } from "sonner";

type Customer = {
  customerId: string;
  name: string;
  email: string;
  phonenumber: string;
};

type Props = {
  onClose: () => void;
  billingId: string;
};

const SendLinkOverlay: React.FC<Props> = ({ onClose, billingId }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
  });

  useEffect(() => {
    const getCustomers = async () => {
      try {
        const res = await api.get(API_ENDPOINTS.CUSTOMERS.GET);
        setCustomers(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch customers", err);
        setCustomers([]);
      }
    };
    getCustomers();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setSelectedCustomerId(selectedId);

    const customer = customers.find((c) => c.customerId === selectedId);
    if (customer) {
      setFormData({
        name: customer.name || "",
        email: customer.email || "",
        phoneNumber: customer.phonenumber || "",
      });
    } else {
      // Reset form if no customer is selected
      setFormData({ name: "", email: "", phoneNumber: "" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.phoneNumber.trim()) {
      alert("Phone number is required");
      return;
    }

    try {
      const payload = {
        billingId: billingId,
        name: formData.name,
        email: formData.email,
        phonenumber: formData.phoneNumber,
      };

      const response = await api.post(
        API_ENDPOINTS.CASHFREE.CREATE_LINK,
        payload
      );
      console.log("Payment link created:", response.data);
      toast.success("Payment link sent successfully!");
      onClose();
    } catch (error) {
      console.error("Error creating payment link:", error);
      toast.error("Failed to send payment link. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-black"
        >
          ✕
        </button>
        <h2 className="text-lg font-semibold mb-4">Send Payment Link</h2>

        <label className="block mb-2 text-sm font-medium text-gray-700">
          Select Customer
        </label>
        <select
          value={selectedCustomerId}
          onChange={handleCustomerSelect}
          className="w-full mb-4 p-2 border rounded"
        >
          <option value="">Select a customer</option>
          {customers.map((c) => (
            <option key={c.customerId} value={c.customerId}>
              {c.name} - {c.phonenumber}
            </option>
          ))}
        </select>

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            name="name"
            placeholder="Name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />
          <input
            type="tel"
            name="phoneNumber"
            placeholder="Phone Number *"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
            className="w-full border p-2 rounded"
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded w-full"
          >
            Send Link
          </button>
        </form>
      </div>
    </div>
  );
};

export default SendLinkOverlay;
